import type { Destination } from '../lib/destinations'

/**
 * The destinations a code can install, and what redeems each one.
 *
 * **The catalogue is not in the bundle.** It is a store the o-cam.app worker
 * reads, and this file is the development fallback plus the shape of a record.
 * Two reasons, and the second is the one that matters:
 *
 *  1. A destination nobody has a code for must not be discoverable by reading
 *     the JavaScript. Shipping the table to every browser and filtering it
 *     client-side is theatre — the list is one devtools pane away.
 *  2. **A platform's limits change without asking us.** Reels' 90 seconds and
 *     TikTok's ten minutes are figures from a particular week; a destination
 *     whose `accepts` lives in a store can be corrected the day it moves,
 *     against installs already on people's phones. In the bundle it would be a
 *     release, and until that release every affected user gets a refusal from
 *     the platform instead of from us.
 *
 * So: production reads KV, and what is written below is what a developer with
 * no KV binding gets, under obviously-fake codes. See `redeem()`.
 */

/** A catalogue row: the destination, and how it is found. */
export interface CatalogueEntry {
  /**
   * SHA-256 of the install code, lowercase hex.
   *
   * **Not a security measure, and must not be mistaken for one.** The codes
   * are short and public (`YT`, `OKB`); a digest of `YT` is guessed by anyone
   * who thinks to try. It is a key-shape convention — one fixed-length,
   * URL-safe KV key whatever the code looks like — and nothing more.
   *
   * A code grants VISIBILITY of a destination and no access to it.
   * Authentication happens at the destination itself, which is what makes a
   * guessable code perfectly acceptable.
   */
  codeHash: string
  destination: Destination
}

/* ── the two existing hosts, as destinations ───────────────────────────── */

/**
 * OKB and Console used to be HOSTS: a hostname decided which one you were
 * using, and you could only ever reach that one. They are now two rows in a
 * table and a user can install both, which is the whole point of the
 * consolidation — one app, several places for a film to go.
 *
 * `shape: 'project'` for both. They are the only destinations that can
 * continue an edit, so they are the only ones offered the sources rather than
 * a finished film.
 */
export const okbDestination: Destination = {
  id: 'okb',
  label: 'OKB',
  kind: 'api-host',
  auth: {
    kind: 'host-session',
    webOrigin: 'https://o-kb.com',
    loginPath: '/auth/login',
  },
  accepts: {
    shape: 'project',
    needs: { title: true, description: true, tags: false },
  },
  taxonomy: {
    level1: { label: 'Space', plural: 'Spaces' },
    level2: { label: 'Input feed', plural: 'Input feeds' },
    // /api/feeds returns every feed; only active notebook feeds accept a post.
    level2Filter: { sourceType: 'notebook', activeOnly: true },
  },
  services: ['transcription', 'metadataGeneration', 'notesAssistant'],
}

export const consoleDestination: Destination = {
  id: 'console',
  label: 'Console',
  kind: 'api-host',
  auth: {
    kind: 'host-session',
    webOrigin: 'https://console.openside.com',
    loginPath: '/login',
  },
  accepts: {
    shape: 'project',
    needs: { title: true, description: true, tags: false },
  },
  taxonomy: {
    level1: { label: 'Brand', plural: 'Brands' },
    level2: { label: 'Folder', plural: 'Folders' },
    // No level2Filter: console's /api/feeds returns only postable folders.
  },
  services: ['transcription', 'metadataGeneration', 'notesAssistant'],
}

/* ── the platforms ─────────────────────────────────────────────────────── */

/**
 * Figures below are from each platform's published API limits and are the
 * values the integration was written against. They are duplicated into KV for
 * production precisely so they can be corrected without a release — treat what
 * is here as a starting point that has already begun to go stale.
 *
 * No `services` on any of them, and none is an oversight: a platform takes a
 * film and gives nothing back. Transcription on a YouTube-only install comes
 * from no destination at all, and the app says so rather than offering a
 * button that cannot work.
 */
export const youtubeDestination: Destination = {
  id: 'youtube',
  label: 'YouTube',
  kind: 'platform',
  auth: {
    kind: 'oauth',
    provider: 'google',
    scopes: ['https://www.googleapis.com/auth/youtube.upload'],
  },
  accepts: {
    shape: 'film',
    // 15 minutes for an unverified account; a verified one gets 12 hours. The
    // low figure is the honest default — being refused by us at 15:01 with a
    // reason beats being refused by YouTube after the upload.
    maxDurationMs: 15 * 60 * 1000,
    maxBytes: 128 * 1024 * 1024 * 1024,
    needs: { title: true, description: true, tags: true },
  },
}

/**
 * YouTube Shorts — the same account, the same upload, different limits.
 *
 * **The API has no Shorts flag.** There is one upload method and no
 * `isShort` field: YouTube decides after the upload, from the file itself —
 * square or taller, and inside the Shorts length. So this is not a second
 * publishing mode, and a user cannot choose it. A vertical 45-second film
 * becomes a Short whichever of these two destinations it was sent to, and
 * nothing in the request can opt out.
 *
 * It is a separate destination anyway, because what it buys is the REFUSAL.
 * Picking Shorts with a 16:9 film says "YouTube Shorts wants 9:16 or square;
 * this film is 16:9" before the render, where the single destination would
 * accept it and quietly publish a regular video. Same provider, same
 * publisher; only `accepts` differs.
 *
 * The three-minute limit was raised from sixty seconds in late 2024 and
 * secondary guides still repeat the old figure. It lives here precisely so it
 * can be corrected without a release.
 */
export const youtubeShortsDestination: Destination = {
  id: 'youtube-shorts',
  label: 'YouTube Shorts',
  kind: 'platform',
  auth: {
    kind: 'oauth',
    // The same Google connection serves both. A user who connected for
    // YouTube does not sign in again for Shorts.
    provider: 'google',
    scopes: ['https://www.googleapis.com/auth/youtube.upload'],
  },
  accepts: {
    shape: 'film',
    maxDurationMs: 3 * 60 * 1000,
    maxBytes: 128 * 1024 * 1024 * 1024,
    // Square or taller. A 16:9 film is not cropped on our side without being
    // asked — it is refused with the reason, and the user decides.
    aspectRatios: [9 / 16, 4 / 5, 1],
    needs: { title: true, description: true, tags: true },
  },
}

export const instagramDestination: Destination = {
  id: 'instagram',
  label: 'Instagram',
  kind: 'platform',
  auth: {
    kind: 'oauth',
    provider: 'instagram',
    scopes: ['instagram_business_content_publish'],
  },
  accepts: {
    shape: 'film',
    maxDurationMs: 90 * 1000,
    maxBytes: 1024 * 1024 * 1024,
    // Reels are vertical. A 16:9 film is not cropped on our side without being
    // asked — it is refused with the reason, and the user decides.
    aspectRatios: [9 / 16],
    // The caption is the only text Instagram takes; there is no title field.
    needs: { title: false, description: true, tags: false },
  },
}

export const tiktokDestination: Destination = {
  id: 'tiktok',
  label: 'TikTok',
  kind: 'platform',
  auth: {
    kind: 'oauth',
    provider: 'tiktok',
    scopes: ['video.publish'],
  },
  accepts: {
    shape: 'film',
    maxDurationMs: 10 * 60 * 1000,
    maxBytes: 4 * 1024 * 1024 * 1024,
    needs: { title: false, description: true, tags: false },
  },
}

/** Every destination this build knows how to talk to, by id. */
export const DESTINATIONS_BY_ID: Readonly<Record<string, Destination>> = {
  okb: okbDestination,
  console: consoleDestination,
  youtube: youtubeDestination,
  'youtube-shorts': youtubeShortsDestination,
  instagram: instagramDestination,
  tiktok: tiktokDestination,
}

/**
 * The development catalogue: plain codes, deliberately.
 *
 * `dev-<id>` for each destination. Not hashed, because hashing these would
 * only pretend they are secrets — they are published right here, and
 * `redeem()` refuses to consult this table outside development. A developer
 * installs any destination without a KV binding; nobody does so on o-cam.app.
 */
export const DEV_CODES: Readonly<Record<string, Destination>> = Object.fromEntries(
  Object.entries(DESTINATIONS_BY_ID).map(([id, destination]) => [`dev-${id}`, destination]),
)
